package org.moxultimate.topwithmox.wallpapercrud.update

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.crud.*
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.wallpaper.data.WallpaperResponse
import org.moxultimate.topwithmox.wallpapercrud.main.WallpaperCRUDState
import org.moxultimate.topwithmox.wallpapercrud.main.WallpaperCRUDViewModel
import org.moxultimate.topwithmox.wallpapercrud.main.WallpaperFormEntry

/**
 * Edit page for a single existing wallpaper.
 * Pre-populates all fields from [wallpaper] and calls PUT /wallpapers/{id} on save.
 *
 * @param wallpaper The wallpaper to edit, reconstructed from nav params by the route initializer.
 */
@Composable
fun WallpaperUpdatePage(wallpaper: WallpaperResponse) {
    val viewModel: WallpaperCRUDViewModel = koinViewModel()
    val uploadState by viewModel.uploadState.collectAsState()
    val navigator = rememberAppNavigator()

    // Pre-populate form from the wallpaper passed via nav params
    var entry by remember {
        mutableStateOf(
            WallpaperFormEntry(
                title = wallpaper.name,
                description = wallpaper.description.orEmpty(),
                type = wallpaper.type,
                series = wallpaper.series,
                tags = wallpaper.tags,
                resolution = wallpaper.resolution,
                videoLink = wallpaper.videoLink.orEmpty(),
            )
        )
    }

    // Show success dialog when upload/delete succeeds
    if (uploadState is WallpaperCRUDState.Success) {
        CRUDSuccessDialog(
            message = (uploadState as WallpaperCRUDState.Success).message,
            onOkay = {
                viewModel.resetState()
                navigator.goToWallpaperCRUD()
            },
        )
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(Colors.White)
            .overflow(Overflow.Auto),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier
                .width(clamp(min = 900.px, value = 80.vw, max = 1400.px))
                .padding(top = 80.px, bottom = 32.px, leftRight = 2.cssRem),
            verticalArrangement = Arrangement.spacedBy(32.px),
        ) {
            // Header row: back button + title
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(4.px)) {
                    SpanText(
                        text = "Edit Wallpaper",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(32.px)
                            .fontWeight(FontWeight.Bold)
                            .moxTextGradient(),
                    )
                    SpanText(
                        text = "ID: ${wallpaper.id}  ·  ${wallpaper.type}  ·  ${wallpaper.series}",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(13.px)
                            .color(Colors.Gray),
                    )
                }
                SpanText(
                    text = "← Back",
                    modifier = PressableButtonStyle.toModifier()
                        .fontFamily(FontFamily)
                        .fontSize(14.px)
                        .color(Colors.Gray)
                        .onClick { navigator.goBack() },
                )
            }

            // Status banner
            when (uploadState) {
                is WallpaperCRUDState.Error -> CRUDStatusBanner(
                    message = (uploadState as WallpaperCRUDState.Error).message,
                    isError = true,
                    onDismiss = { viewModel.resetState() },
                )
                else -> {}
            }

            // Image + Thumbnail + Panorama replacement zones
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.px),
            ) {
                Box(modifier = Modifier.weight(1f)) {
                    CRUDExistingDropZone(
                        label = "Image",
                        existingUrl = wallpaper.imagePath.orEmpty(),
                        file = entry.imageFile,
                        onFileDropped = { file ->
                            detectResolutionFromFile(file) { res ->
                                entry = entry.copy(imageFile = file, resolution = res)
                            }
                        },
                        onFileRemoved = { entry = entry.copy(imageFile = null, resolution = wallpaper.resolution) },
                    )
                }
                Box(modifier = Modifier.weight(1f)) {
                    CRUDExistingDropZone(
                        label = "Thumbnail",
                        existingUrl = wallpaper.thumbnailPath.orEmpty(),
                        file = entry.thumbnailFile,
                        onFileDropped = { entry = entry.copy(thumbnailFile = it) },
                        onFileRemoved = { entry = entry.copy(thumbnailFile = null) },
                    )
                }
                Box(modifier = Modifier.weight(1f)) {
                    CRUDExistingDropZone(
                        label = "Panorama (360°, optional)",
                        existingUrl = wallpaper.panoramaPath.orEmpty(),
                        file = entry.panoramaFile,
                        onFileDropped = { entry = entry.copy(panoramaFile = it) },
                        onFileRemoved = { entry = entry.copy(panoramaFile = null) },
                    )
                }
            }

            // Row 1: Title + Type
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.px)) {
                Box(modifier = Modifier.weight(1f)) {
                    CRUDFormField(
                        label = "Title *",
                        value = entry.title,
                        placeholder = "Wallpaper title",
                        onValueChange = { v -> entry = entry.copy(title = v, tags = buildAutoTags(v, entry.series, entry.type)) },
                    )
                }
                Box(modifier = Modifier.weight(1f)) {
                    CRUDTypeDropdown(
                        selected = entry.type,
                        onSelected = { v -> entry = entry.copy(type = v, tags = buildAutoTags(entry.title, entry.series, v)) },
                    )
                }
            }

            // Row 2: Series + Resolution
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.px)) {
                Box(modifier = Modifier.weight(1f)) {
                    CRUDFormField(
                        label = "Series *",
                        value = entry.series,
                        placeholder = "Series name",
                        onValueChange = { v -> entry = entry.copy(series = v, tags = buildAutoTags(entry.title, v, entry.type)) },
                    )
                }
                Box(modifier = Modifier.weight(1f)) {
                    CRUDFormField(
                        label = "Resolution",
                        value = entry.resolution,
                        placeholder = "e.g. 1080x1920",
                        onValueChange = { entry = entry.copy(resolution = it) },
                    )
                }
            }

            // Row 3: Tags + Video Link
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.px)) {
                Box(modifier = Modifier.weight(1f)) {
                    CRUDFormField(
                        label = "Tags",
                        value = entry.tags,
                        placeholder = "Frieren | Phone Wallpaper | 4K",
                        onValueChange = { entry = entry.copy(tags = it) },
                    )
                }
                Box(modifier = Modifier.weight(1f)) {
                    CRUDFormField(
                        label = "Video Link",
                        value = entry.videoLink,
                        placeholder = "YouTube URL (optional)",
                        onValueChange = { entry = entry.copy(videoLink = it) },
                    )
                }
            }

            // Description
            CRUDFormField(
                label = "Description",
                value = entry.description,
                placeholder = "Short description",
                onValueChange = { entry = entry.copy(description = it) },
            )

            // Save + Delete row
            val isLoading = uploadState is WallpaperCRUDState.Loading
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                GradientButton(
                    text = if (isLoading) "Saving…" else "Save Changes",
                    disabled = !entry.isValidForUpdate || isLoading,
                    onClick = {
                        if (entry.isValidForUpdate && !isLoading) {
                            viewModel.updateWallpaper(wallpaper.id, entry)
                        }
                    },
                )
                GradientButton(
                    text = if (isLoading) "Please wait…" else "🗑 Delete",
                    disabled = isLoading,
                    onClick = { if (!isLoading) viewModel.deleteWallpaper(wallpaper.id) },
                )
            }
        }
    }
}

